import { rtkQuery } from '~/services/rtkQuery';
import { TAGS } from '~/services/rtkQuery/tags';

import {
  CheckCompanyExistenceResponse,
  CheckCompanyExistenceVariables,
  CreateCompanyResponse,
  CreateCompanyVariables,
  DeleteCompanyResponse,
  DeleteCompanyVariables,
  DeleteDraftCompanyResponse,
  DeleteDraftCompanyVariables,
  GetCeoEducationsResponse,
  GetClosedAndRejectedCompaniesResponse,
  GetCompaniesVariables,
  GetCompanyAnalyticsResponse,
  GetCompanyHistoryChangeResponse,
  GetCompanyHistoryChangeVariables,
  GetCompanyHistoryResponse,
  GetCompanyHistoryVariables,
  GetCompanyInferenceResponse,
  GetCompanyInferenceVariables,
  GetCompanyResponse,
  GetCompanyVariables,
  GetCompetitorsResponse,
  GetCompetitorsVariables,
  GetDraftCompaniesResponse,
  GetDraftCompanyResponse,
  GetDraftCompanyVariables,
  GetFirstFinancingDealTypesResponse,
  GetIndustriesResponse,
  GetInProgressCompaniesResponse,
  GetInvestorsResponse,
  GetInvestorsVariables,
  GetPendingCompaniesResponse,
  GetPitchbookCompanyDataByDealIdsVariables,
  GetPitchbookCompanyDataResponse,
  GetPitchbookCompanyDataVariables,
  GetPitchbookCompanyRawDealsResponse,
  GetPitchbookCompanyRawDealsVariables,
  GetPitchbookCompanyResponse,
  GetPitchbookCompanyVariables,
  GetSecurityTypesResponse,
  LockCompanyResponse,
  LockCompanyVariables,
  SaveCompanyAsDraftResponse,
  SaveCompanyAsDraftVariables,
  SearchPitchbookCompanyResponse,
  SearchPitchbookCompanyVariables,
  UnlockCompanyResponse,
  UnlockCompanyVariables,
  UpdateCompanyStatusResponse,
  UpdateCompanyStatusVariables,
} from './types';

export const companyApi = rtkQuery.injectEndpoints({
  overrideExisting: true,
  endpoints: builder => ({
    getCompetitors: builder.query<GetCompetitorsResponse, GetCompetitorsVariables>({
      query: data => ({
        url: 'company/competitors',
        method: 'GET',
        params: data,
      }),
    }),
    getInvestors: builder.query<GetInvestorsResponse, GetInvestorsVariables>({
      query: data => ({
        url: 'company/investors',
        method: 'GET',
        params: data,
      }),
    }),
    getIndustries: builder.query<GetIndustriesResponse, void>({
      query: () => 'company/industries',
    }),
    getFirstFinancingDealTypes: builder.query<GetFirstFinancingDealTypesResponse, void>({
      query: () => 'company/finance-dealtype',
    }),
    getCeoEducations: builder.query<GetCeoEducationsResponse, void>({
      query: () => 'company/ceo-education',
    }),
    createCompany: builder.mutation<CreateCompanyResponse, CreateCompanyVariables>({
      query: data => ({
        url: 'company/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (response, error, arg) =>
        arg.id ? [{ type: TAGS.COMPANY_HISTORY, id: arg.id }, TAGS.COMPANIES] : [TAGS.COMPANIES],
    }),
    saveCompanyAsDraft: builder.mutation<SaveCompanyAsDraftResponse, SaveCompanyAsDraftVariables>({
      query: data => ({
        url: 'company/draft',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (response, error, arg) =>
        arg.id
          ? [
              { type: TAGS.DRAFT_COMPANY, id: arg.id },
              { type: TAGS.COMPANY_HISTORY, id: arg.id },
              { type: TAGS.COMPANY_HISTORY_CHANGE, id: arg.id },
              TAGS.DRAFT_COMPANIES,
            ]
          : [TAGS.DRAFT_COMPANIES],
    }),
    searchPitchbookCompany: builder.query<
      SearchPitchbookCompanyResponse,
      SearchPitchbookCompanyVariables
    >({
      query: data => ({
        url: 'company/search',
        method: 'GET',
        params: data,
      }),
    }),
    getPitchbookCompany: builder.mutation<
      GetPitchbookCompanyResponse,
      GetPitchbookCompanyVariables
    >({
      query: data => ({
        url: 'company/get/bio',
        method: 'GET',
        params: data,
      }),
    }),
    getPitchbookCompanyData: builder.query<
      GetPitchbookCompanyDataResponse,
      GetPitchbookCompanyDataVariables
    >({
      query: data => ({
        url: 'company/fetch/data',
        method: 'GET',
        params: data,
      }),
    }),
    getPitchbookCompanyDataByDealIds: builder.query<
      GetPitchbookCompanyDataResponse,
      GetPitchbookCompanyDataByDealIdsVariables
    >({
      query: ({ pbId, ...data }) => ({
        url: 'company/fetch/data',
        method: 'POST',
        params: { pbId },
        body: data,
      }),
    }),
    getDraftCompanies: builder.query<GetDraftCompaniesResponse, GetCompaniesVariables>({
      query: data => ({
        url: 'company/list',
        method: 'GET',
        params: {
          status: 'draft',
          ...data,
        },
      }),
      providesTags: [TAGS.COMPANIES, TAGS.DRAFT_COMPANIES],
    }),
    getInProgressCompanies: builder.query<GetInProgressCompaniesResponse, GetCompaniesVariables>({
      query: data => ({
        url: 'company/list',
        method: 'GET',
        params: {
          status: 'in_progress',
          ...data,
        },
      }),
      providesTags: [TAGS.COMPANIES],
    }),
    getClosedAndRejectedCompanies: builder.query<
      GetClosedAndRejectedCompaniesResponse,
      GetCompaniesVariables
    >({
      query: data => ({
        url: 'company/list',
        method: 'GET',
        params: {
          status: ['closed', 'rejected'],
          ...data,
        },
      }),
      providesTags: [TAGS.COMPANIES],
    }),
    getPendingCompanies: builder.query<GetPendingCompaniesResponse, GetCompaniesVariables>({
      query: data => ({
        url: 'company/list',
        method: 'GET',
        params: {
          status: 'pending',
          ...data,
        },
      }),
    }),
    updateCompanyStatus: builder.mutation<
      UpdateCompanyStatusResponse,
      UpdateCompanyStatusVariables
    >({
      query: ({ id, ...data }) => ({
        url: `company/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (response, error, arg) => [
        { type: TAGS.COMPANY, id: arg.id },
        { type: TAGS.COMPANY_HISTORY, id: arg.id },
        { type: TAGS.COMPANY_HISTORY_CHANGE, id: arg.id },
        TAGS.COMPANIES,
      ],
    }),
    deleteCompany: builder.mutation<DeleteCompanyResponse, DeleteCompanyVariables>({
      query: data => ({
        url: `company/${data.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [TAGS.COMPANIES],
    }),
    getCompany: builder.query<GetCompanyResponse, GetCompanyVariables>({
      query: data => `company/${data.id}`,
      providesTags: (response, error, arg) => [{ type: TAGS.COMPANY, id: arg.id }],
    }),
    getDraftCompany: builder.query<GetDraftCompanyResponse, GetDraftCompanyVariables>({
      query: data => ({
        url: `company/draft/${data.id}`,
        method: 'GET',
      }),
      providesTags: (response, error, arg) => [{ type: TAGS.DRAFT_COMPANY, id: arg.id }],
    }),
    deleteDraftCompany: builder.mutation<DeleteDraftCompanyResponse, DeleteDraftCompanyVariables>({
      query: data => ({
        url: `company/draft/${data.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [TAGS.DRAFT_COMPANIES, TAGS.COMPANIES],
    }),
    getCompanyHistory: builder.query<GetCompanyHistoryResponse, GetCompanyHistoryVariables>({
      query: data => ({
        url: 'company/history',
        method: 'GET',
        params: data,
      }),
      providesTags: (response, error, arg) => [{ type: TAGS.COMPANY_HISTORY, id: arg.id }],
    }),
    getCompanyHistoryChange: builder.query<
      GetCompanyHistoryChangeResponse,
      GetCompanyHistoryChangeVariables
    >({
      query: data => ({
        url: 'company/difference',
        method: 'GET',
        params: data,
      }),
      providesTags: (response, error, arg) => [{ type: TAGS.COMPANY_HISTORY_CHANGE, id: arg.id }],
    }),
    getCompanyInference: builder.query<GetCompanyInferenceResponse, GetCompanyInferenceVariables>({
      query: data => `company/inference/${data.id}`,
    }),
    getCompanyAnalytics: builder.query<GetCompanyAnalyticsResponse, void>({
      query: () => 'company/analytics',
    }),
    checkCompanyExistence: builder.mutation<
      CheckCompanyExistenceResponse,
      CheckCompanyExistenceVariables
    >({
      query: data => ({
        url: 'company/existence',
        method: 'GET',
        params: data,
      }),
    }),
    getPitchbookCompanyRawDeals: builder.query<
      GetPitchbookCompanyRawDealsResponse,
      GetPitchbookCompanyRawDealsVariables
    >({
      query: data => ({
        url: 'company/raw-deals',
        method: 'GET',
        params: data,
      }),
    }),
    getSecurityTypes: builder.query<GetSecurityTypesResponse, void>({
      query: () => 'company/security-types',
    }),
    lockCompany: builder.mutation<LockCompanyResponse, LockCompanyVariables>({
      query: data => ({
        url: 'company/lock',
        method: 'POST',
        params: data,
      }),
    }),
    unlockCompany: builder.mutation<UnlockCompanyResponse, UnlockCompanyVariables>({
      query: data => ({
        url: 'company/unlock',
        method: 'POST',
        params: data,
      }),
    }),
  }),
});

export const {
  useGetCompetitorsQuery,
  useGetInvestorsQuery,
  useGetIndustriesQuery,
  useGetFirstFinancingDealTypesQuery,
  useGetCeoEducationsQuery,
  useCreateCompanyMutation,
  useSaveCompanyAsDraftMutation,
  useSearchPitchbookCompanyQuery,
  useGetPitchbookCompanyMutation,
  useLazyGetPitchbookCompanyDataQuery,
  useLazyGetPitchbookCompanyDataByDealIdsQuery,
  useGetDraftCompaniesQuery,
  useGetInProgressCompaniesQuery,
  useGetClosedAndRejectedCompaniesQuery,
  useGetPendingCompaniesQuery,
  useUpdateCompanyStatusMutation,
  useDeleteCompanyMutation,
  useGetCompanyQuery,
  useLazyGetDraftCompanyQuery,
  useDeleteDraftCompanyMutation,
  useGetCompanyHistoryQuery,
  useGetCompanyHistoryChangeQuery,
  useGetCompanyInferenceQuery,
  useGetCompanyAnalyticsQuery,
  useCheckCompanyExistenceMutation,
  useLazyGetPitchbookCompanyRawDealsQuery,
  useGetSecurityTypesQuery,
  useLockCompanyMutation,
  useUnlockCompanyMutation,
} = companyApi;
